import { FileInput } from "lucide-react";
import { Link, useLocation } from "react-router";
import ProgressWithPercent from "@/shared/components/ProgressWithPercent/ProgressWithPercent";
import { JOBS_PAGE_PATH } from "@/shared/configs/routes.config";
import { Badge } from "@/shared/ui/Badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/shared/ui/Dialog";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/shared/ui/Tooltip";
import { STATUS_COLORS } from "../../configs/constants";
import { useJobContext } from "../../hooks/useJobContext";
import { useJobData } from "../../hooks/useJobData";
import { isComplete, isInProgress, isCancelling } from "../../utils/job-status.utils";
import JobModalButtons from "./JobModalButtons";
import JobResults from "./JobResults";

const JobModalHeader = ({ title, inputFileAttachmentUrl, jobUuid }: {
  title: string
  inputFileAttachmentUrl: string | null | undefined
  jobUuid: string
}) => (
  <DialogHeader className="px-6">
    <DialogTitle className="flex flex-col w-full gap-1">
      <span className="flex items-center gap-4">
        {title}
        {inputFileAttachmentUrl && (
          <Tooltip>
            <TooltipTrigger asChild>
              <a
                href={inputFileAttachmentUrl}
                download
                title="Télécharger le fichier importé"
                className="text-gray-600 hover:text-gray-800"
              >
                <FileInput size={16} />
              </a>
            </TooltipTrigger>
            <TooltipContent side="right">
              <p>Télécharger le fichier importé</p>
            </TooltipContent>
          </Tooltip>
        )}
      </span>
      <span className="text-xs text-gray-500">
        Ref :
        {" "}
        {jobUuid}
      </span>
    </DialogTitle>
  </DialogHeader>
);

const JobModal = () => {
  const { currentJob, isJobModalOpen, closeJobModal } = useJobContext();
  const location = useLocation();

  const { mergedJob: job } = useJobData(currentJob);

  if (!currentJob || !job) return null;
  const {
    title,
    comments,
    status_code,
    status_label,
    percent, result,
    job_piece,
  } = job;

  const statusColor = STATUS_COLORS[status_code] ?? STATUS_COLORS.default;
  const isJobInProgress = isInProgress(job);
  // A cancellation runs in progress but, unlike an import, keeps the dialog's close (✕) available
  // so the user can dismiss and let the reverse-progress finish in the background.
  const isJobCancelling = isCancelling(job);
  const isOpenInJobsPage = location.pathname.includes(JOBS_PAGE_PATH);
  // `job_piece` is the uploaded source file for import jobs, but export jobs reuse
  // it to retain the result blob — so the "fichier importé" shortcut is misleading
  // there (the result already has its own "Télécharger le fichier" button).
  const inputFileAttachmentUrl = job.resource.includes("EXPORT") ? null : job_piece?.url;

  const customDialogClosingEffectOnAllPagesExceptJobsPage = isJobInProgress
    ? ` data-[state=open]:animate-in data-[state=open]:duration-200 
        data-[state=closed]:animate-out
        data-[state=closed]:duration-300
        data-[state=closed]:fade-out-5 
        data-[state=closed]:slide-out-to-left-[125%] 
        data-[state=closed]:zoom-out-[0.25] 
      `
    : "";

  return (
    <Dialog
      open={isJobModalOpen}
      onOpenChange={closeJobModal}
    >
      <DialogContent
        className={`px-0 overflow-x-auto
                    ${isJobInProgress && !isJobCancelling ? "[&>button]:hidden" : ""}
                    ${customDialogClosingEffectOnAllPagesExceptJobsPage}`}
      >
        <JobModalHeader
          title={title}
          inputFileAttachmentUrl={inputFileAttachmentUrl}
          jobUuid={currentJob.uuid}
        />

        <DialogDescription asChild>
          <div>
            <div className="flex items-center gap-2 px-6">
              <Badge className={statusColor}>{status_label}</Badge>
              <ProgressWithPercent percent={percent} />
            </div>

            <div className="my-6">
              {isJobInProgress && (
                <div className="flex flex-col items-center gap-2 px-6 text-pretty text-center">
                  <p>Votre demande est en cours d'exécution, et peut prendre quelques minutes.</p>
                  <p>La fermeture de cette fenêtre ne stoppera pas cette tâche en cours.</p>
                  {!isOpenInJobsPage && (
                    <p>
                      Vous pourrez la retrouver dans la section
                      {" "}
                      <Link
                        to={JOBS_PAGE_PATH}
                        className="text-blue-500"
                        onClick={closeJobModal}
                      >
                        Tâches
                      </Link>
                      .
                    </p>
                  )}
                </div>
              )}

              {isComplete(job) && result?.file_expired && (
                <p className="px-6 text-center text-sm text-gray-500">
                  Le fichier de cet export a expiré et n'est plus disponible au téléchargement.
                  Relancez l'export pour le régénérer.
                </p>
              )}

              {isComplete(job) && !!result && (
                <JobResults
                  result={result}
                  fallbackComments={comments}
                />
              )}
            </div>
          </div>
        </DialogDescription>

        <DialogFooter>
          <JobModalButtons
            job={job}
            onCloseModal={closeJobModal}
          />
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default JobModal;
